import React, { useEffect, useRef, useState } from 'react';
import { get } from 'lodash';
import moment from 'moment';
import styled from 'styled-components';
import { useReorderAddOnsMutation } from '../../../features/addons/addonApiSlice';
import StyledText from '../styledComponents/StyledText';
import { Col, Row } from 'react-bootstrap';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';


const StyledRow = styled.div`
  /* By default, show the row */
  display: flex;
  margin: 0px !important;
  padding: 0px !important;

  /* Hide the row on larger screens (≥ 767px) */
  @media (min-width: 767px) {
    display: none;
  }
`;

const StyledAnotherRow = styled(Row)`
  /* Hide the row on smaller screens (< 768px) */
  display: none;

  /* Show the row on larger screens (≥ 767px) */
  @media (min-width: 767px) {
    display: flex;
  }
`;

const SortableRow = SortableElement(
  ({
    item,
    columns,
    index,
    renderCell,
    rowHighlighter,
    createKey,
    highlightRowsOnHover,
    onRowClick,
    pointer,
  }) => (
    <tr
      key={index + '-tb-row'}
      className={`${highlightRowsOnHover ? 'hoverable-row' : ''} ${
        onRowClick && pointer ? 'cursor-pointer' : ''
      }`}
      {...(onRowClick && {
        onClick: () => onRowClick(item, index),
      })}
    >
      {columns.map((column, i) => (
        <StyledTableData
          key={createKey(i, item, column)}
          {...(column?.alignBody && {
            className: column?.alignBody,
          })}
          {...(rowHighlighter && {
            highlighted: rowHighlighter(item, index),
          })}
          {...(column.style && { style: column.style })}
          style={{
            ...(column.fixed
              ? {
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  backgroundColor: 'white',
                }
              : {}), // Fix the left-side column
            ...column.style, // Apply any additional custom styles
            userSelect: 'none',
            cursor: 'grab',
          }}
        >
          {renderCell(item, column, index)}
        </StyledTableData>
      ))}
    </tr>
  ),
);

const SortableTableBody = SortableContainer(
  ({
    data,
    columns,
    renderCell,
    rowHighlighter,
    createKey,
    highlightRowsOnHover,
    onRowClick,
    pointer,
  }) => {
    return (
      <StyledTBody>
        {data?.map((item, index) => (
          <SortableRow
            key={`row-${item?.id || index}`}
            index={index}
            item={item}
            columns={columns}
            renderCell={renderCell}
            rowHighlighter={rowHighlighter}
            createKey={createKey}
            highlightRowsOnHover={highlightRowsOnHover}
            onRowClick={onRowClick}
            pointer={pointer}
          />
        ))}
      </StyledTBody>
    );
  },
);

const TableBody = ({
  data,
  columns,
  striped,
  onRowClick,
  rowHighlighter,
  highlightRowsOnHover,
  isCatalogue,
  pointer = true,
  addOnPage = false,
  websiteOrders,
}) => {
  const [reorderAddOns] = useReorderAddOnsMutation();
  const [addOnData, setAddOnData] = useState(data);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const renderCell = (item, column, index) => {
    const modifiedItem = {
      ...item,
      categoryDTO: {
        ...item.categoryDTO,
        name: isCatalogue
          ? item?.productRecognition
            ? `Designed by ${item?.productRecognition}` || ''
            : 'Designed by BoostFloral'
          : item?.categoryDTO?.name || '-',
      },
    };
    if (column.content) return column.content(modifiedItem, index);
    if (column.path === 'createdAt' || column.path === 'updatedAt') {
      return formatDate(get(item, column.path));
    }
    return get(item, column.path) ?? '-';
  };

  const createKey = (i, item, column) => {
    return item?.id + (column?.path || column?.key) + '-td-' + i;
  };

  const formatDate = (date) => moment(new Date(date)).format('Do MMM YYYY');

  useEffect(() => {
    if (addOnPage) {
      setAddOnData(data);
    }
  }, [data]);

  const onSortEnd = async ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return; // If same position, do nothing

    const newItems = [...addOnData];
    const [removed] = newItems.splice(oldIndex, 1); // Remove item from old position
    newItems.splice(newIndex, 0, removed); // Insert at new position

    const items = newItems.map((item, index) => ({
      ...item,
      order: index + 1, // Update order based on new position
    }));

    setAddOnData(items); // Update state

    // API Call to update backend ordering
    try {
      const { data: responseData } = await reorderAddOns({
        data: items.map((item) => ({ id: item.id, order: item.order })),
      });
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  return (
    <>
      { addOnPage ? (
        <SortableTableBody
          data={addOnData}
          columns={columns}
          renderCell={renderCell}
          onSortEnd={onSortEnd}
          useDragHandle={false}
          createKey={createKey}
          rowHighlighter={rowHighlighter}
          highlightRowsOnHover={highlightRowsOnHover}
          onRowClick={onRowClick}
          pointer={pointer}
          shouldCancelStart={(e) => {
            return e.target.closest('.no-drag') !== null;
          }}
          helperClass="dragging-row"
        />
      ) : (
        <StyledTBody
          className={`mt-0 mt-md-2 rounded-2 ${striped ? 'striped' : ''}`}
        >
          {data?.map((item, index) => (
            <tr
              key={index + '-tb-row'}
              className={`${highlightRowsOnHover ? 'hoverable-row' : ''} ${
                onRowClick && pointer ? 'cursor-pointer' : ''
              } py-2`}
              {...(onRowClick && { onClick: () => onRowClick(item, index) })}
            >
              {columns.map((column, i) => (
                <StyledTableData
                  websiteOrders={websiteOrders}
                  className={isMobile ? 'py-0' : ''}
                  key={createKey(i, item, column)}
                  {...(column?.alignBody && { className: column?.alignBody })}
                  {...(rowHighlighter && {
                    highlighted: rowHighlighter(item, index),
                  })}
                  {...(column.style && { style: column.style })}
                  style={{
                    ...(isMobile && {
                      borderTop: 'none',
                    }),
                    ...(column.fixed
                      ? {
                          position: 'sticky',
                          left: 0,
                          zIndex: 1,
                          backgroundColor: 'white',
                        }
                      : {}), // Fix the left-side column
                    ...column.style, // Apply any additional custom styles
                  }}
                >
                  <StyledRow className="w-100 p-0 m-0">
                    <div
                      className="d-flex flex-row justify-content-start align-items-center p-0 m-0"
                      style={{ width: '120px' }}
                      // xs={5}
                    >
                      <StyledText
                        as="h5"
                        $fontSize="14px"
                        color="heading"
                        $fontWeight="500"
                        margin="0px"
                      >
                        {column?.label}
                      </StyledText>
                    </div>
                    <div
                      style={{
                        flexGrow: 1,
                        maxWidth: 'calc(100% - 120px)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      className="d-flex flex-row justify-content-start align-items-center py-1 px-0 m-0"
                    >
                      <StyledText
                        as="h5"
                        $fontSize="14px"
                        color="bodyMedium"
                        $fontWeight="500"
                        className="m-0 w-100 d-flex flex-column justify-content-start align-items-start"
                      >
                        {renderCell(item, column, index)}
                      </StyledText>
                    </div>
                  </StyledRow>

                  <StyledAnotherRow>
                    {renderCell(item, column, index)}
                  </StyledAnotherRow>
                </StyledTableData>
              ))}
            </tr>
          ))}
        </StyledTBody>
      )}
    </>
  );
};

const StyledTBody = styled.tbody`
  background-color: ${({ theme }) => theme?.white};
  tr {
    &:last-child {
      td {
        border-bottom: 0;
      }
    }

    td {
      vertical-align: middle;
    }
  }

  &.striped {
    tr:nth-child(even) {
      td {
        background-color: rgba(0, 0, 0, 0.03);
      }
    }
  }

  .hoverable-row:hover {
    td {
      background-color: rgba(0, 0, 0, 0.1);
    }
  }

  ${({ highlighted }) =>
    highlighted &&
    `
      background-color: rgba(0, 0, 0, 0.3);
    `}
`;

const StyledTableData = styled.td`
  padding: ${({ websiteOrders }) =>
    websiteOrders ? '12px 16px' : '12px 24px'} !important;
  @media (max-width: 500px) {
    padding: 4px 16px !important;
    margin 0px !important;
  }
  ${({ highlighted }) =>
    highlighted &&
    `
        background-color: rgba(0, 0, 0, 0.2) !important;
    `}
`;

export default TableBody;
